import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import {
  Button,
  Empty,
  ErrorBox,
  FlashMessage,
  Loading,
  Modal,
  Money,
  ProSearch,
  Row,
  Select,
  StatusBadge,
  Textarea,
} from '@/components/ui';
import { formatDateTime, formatNumber, str } from '@/utils/format';
import { COMPANY_LABELS, KIND_LABELS, SOURCE_LABELS, labelOf } from '@/utils/labels';

type OrdersMeta = {
  page: number;
  pages: number;
  total: number;
  stats: {
    processing?: number | string;
    success_count?: number | string;
    failed_count?: number | string;
    rejected_count?: number | string;
    refunded_count?: number | string;
  };
};

const STAGE_LABELS: Record<string, string> = {
  queued: 'في الطابور',
  assigned: 'استلمه الجهاز',
  executing: 'بدأ التنفيذ',
  review: 'بانتظار التحقق من النتيجة',
  completed: 'اكتمل التنفيذ',
  failed: 'تعذر التنفيذ',
};

function needsManualReview(order: JsonMap): boolean {
  const failure = str(order.executor_failure_code, '');
  return Boolean(
    order.execution_uncertain ||
      order.review_required ||
      str(order.display_status) === 'pending_review' ||
      ['claim_timeout', 'ussd_timeout', 'missing_confirmation_phrase'].includes(failure),
  );
}

function parseJsonObject(value: unknown): JsonMap {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as JsonMap;
  if (typeof value !== 'string' || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as JsonMap) : {};
  } catch {
    return {};
  }
}

function nestedMessage(value: unknown, depth = 0): string {
  if (depth > 4 || value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = nestedMessage(item, depth + 1);
      if (found) return found;
    }
    return '';
  }
  if (typeof value !== 'object') return '';
  const obj = value as JsonMap;
  for (const key of [
    '_admin_response_text',
    '_internal_message',
    'message',
    'note',
    'notes',
    'reason',
    'result',
    'reply',
    'response',
    'status_text',
  ]) {
    const found = nestedMessage(obj[key], depth + 1);
    if (found) return found;
  }
  for (const key of ['data', 'order', 'item', 'result_data', 'payload', 'response_data', 'info']) {
    const found = nestedMessage(obj[key], depth + 1);
    if (found) return found;
  }
  return '';
}

function productOrderNote(order: JsonMap): string {
  const fromApi = str(order.api_response, '').trim();
  if (fromApi && fromApi !== '—') return fromApi;
  const payload = parseJsonObject(order.response_json);
  return nestedMessage(payload).trim();
}

function providerStatusLabel(status: unknown): string {
  const key = str(status, '');
  if (!key || key === '—') return '—';
  if (key === 'waiting_provider_balance') return 'بانتظار توفر رصيد المصدر';
  return key;
}

function orderCustomerName(order: JsonMap): string {
  const user = str(order.user_name, '').trim();
  const display = str(order.customer_display_name, '').trim();
  return user || display || '—';
}

function orderCustomerAlias(order: JsonMap): string {
  const user = str(order.user_name, '').trim();
  const display = str(order.customer_display_name, '').trim();
  if (display && display !== user) return display;
  return '';
}

function shortExecutorLabel(order: JsonMap): string {
  if (str(order.source_provider) !== 'kazeti_ussd') return '—';
  return str(
    order.executor_device_name,
    order.executor_device_id ? `#${str(order.executor_device_id)}` : 'بانتظار',
  );
}

function isFailedLike(order: JsonMap): boolean {
  return ['failed', 'rejected'].includes(str(order.status));
}

function canRetryOrder(order: JsonMap): boolean {
  return str(order.status) === 'failed';
}

function canRefundOrder(order: JsonMap): boolean {
  return ['failed', 'processing'].includes(str(order.status)) && !order.has_active_claim;
}

export function UnitsOrdersScreen() {
  const [rows, setRows] = useState<JsonMap[]>([]);
  const [meta, setMeta] = useState<OrdersMeta>({
    page: 1,
    pages: 1,
    total: 0,
    stats: {},
  });
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [company, setCompany] = useState('all');
  const [kind, setKind] = useState('all');
  const [source, setSource] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [flash, setFlash] = useState('');
  const [selected, setSelected] = useState<JsonMap | null>(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) {
        setRefreshing(true);
        setError('');
      }
      const res = await admin.orders({
        page,
        q,
        status,
        company,
        kind,
        source,
      });
      if (!silent) setRefreshing(false);
      if (!res.success) {
        if (!silent) {
          setError(errorMessage(res));
          setLoading(false);
        }
        return;
      }
      const nextRows = pickList(res, 'data', 'orders', 'items');
      setRows(nextRows);
      setSelected((prev) => {
        if (!prev) return null;
        return nextRows.find((row) => str(row.id) === str(prev.id)) ?? prev;
      });
      const nextMeta = (res.meta && typeof res.meta === 'object' ? res.meta : {}) as JsonMap;
      const stats =
        nextMeta.stats && typeof nextMeta.stats === 'object'
          ? (nextMeta.stats as OrdersMeta['stats'])
          : {};
      setMeta({
        page: Number(nextMeta.page ?? page) || 1,
        pages: Number(nextMeta.pages ?? 1) || 1,
        total: Number(nextMeta.total ?? 0) || 0,
        stats,
      });
      setLoading(false);
    },
    [page, q, status, company, kind, source],
  );

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(true), 4000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [q, status, company, kind, source]);

  function notify(message: string) {
    setFlash(message);
  }

  async function retryOrder(order: JsonMap) {
    const body: JsonMap = { order_id: order.id, id: order.id };
    if (needsManualReview(order)) {
      if (
        !window.confirm(
          `هل تأكدت أن الرصيد لم يصل إلى ${str(order.target)}؟ سيتم تنفيذ الطلب مرة أخرى.`,
        )
      ) {
        return;
      }
      const note = window.prompt('ملاحظة التحقق:', 'تم التأكد من عدم وصول الرصيد.');
      if (!note || note.trim().length < 8) {
        notify('ملاحظة التحقق مطلوبة قبل إعادة المحاولة.');
        return;
      }
      body.confirm_not_delivered = true;
      body.verification_note = note.trim();
    }
    setBusyId(str(order.id));
    const res = await admin.rechargeRetry(body);
    setBusyId(null);
    notify(errorMessage(res, res.success ? 'تمت العملية.' : 'تعذر إعادة المحاولة.'));
    void load(true);
  }

  async function rejectOrder(order: JsonMap) {
    if (order.has_active_claim) {
      notify('الطلب قيد التنفيذ على الهاتف الآن. انتظر وصول النتيجة قبل الرفض.');
      return;
    }
    const reason = window.prompt('سبب رفض الطلب', 'تم رفض الطلب من الإدارة.');
    if (reason === null) return;
    const body: JsonMap = { order_id: order.id, id: order.id, reason };
    if (needsManualReview(order)) {
      if (
        !window.confirm(
          `هل تأكدت أن الرصيد لم يصل إلى ${str(order.target)}؟ سيتم إلغاء الطلب وإرجاع المبلغ.`,
        )
      ) {
        return;
      }
      const note = window.prompt('ملاحظة التحقق:', 'تم التأكد من عدم وصول الرصيد.');
      if (!note || note.trim().length < 8) {
        notify('ملاحظة التحقق مطلوبة قبل إرجاع المبلغ.');
        return;
      }
      body.confirm_not_delivered = true;
      body.verification_note = note.trim();
    }
    setBusyId(str(order.id));
    const res = await admin.rechargeReject(body);
    setBusyId(null);
    notify(errorMessage(res, res.success ? 'تمت العملية.' : 'تعذر رفض الطلب.'));
    void load(true);
  }

  async function markSuccess(order: JsonMap) {
    if (
      !window.confirm(`اعتماد الطلب #${str(order.id)} كناجح؟ تأكد من وصول الرصيد إلى ${str(order.target)}.`)
    ) {
      return;
    }
    const note = window.prompt('ملاحظة التحقق:', 'تم التأكد من وصول الرصيد.');
    if (!note || note.trim().length < 8) {
      notify('ملاحظة التحقق مطلوبة لاعتماد النجاح.');
      return;
    }
    setBusyId(str(order.id));
    const res = await admin.rechargeMarkSuccess({
      order_id: order.id,
      id: order.id,
      verification_note: note.trim(),
      response_text: order.ussd_response ?? '',
    });
    setBusyId(null);
    notify(errorMessage(res, res.success ? 'تمت العملية.' : 'تعذر اعتماد النجاح.'));
    void load(true);
  }

  if (loading) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={() => void load()} />;

  const stats = meta.stats ?? {};
  const currentPage = meta.page || page;

  return (
    <ScreenFrame
      mode="admin"
      icon="deposit"
      title="طلبات الوحدات"
      subtitle="متابعة طلبات شحن الوحدات والفواتير والنقد عبر USSD وAPI Syria."
      actions={
        <Button type="button" variant="secondary" disabled={refreshing} onClick={() => void load()}>
          <Icon name="refresh" size={18} /> {refreshing ? 'جاري التحديث...' : 'تحديث الآن'}
        </Button>
      }
    >
      {flash ? <p className="ok">{flash}</p> : null}

      <MetricGrid>
        <MetricCard
          title="إجمالي النتائج"
          value={String(meta.total || 0)}
          icon={<Icon name="chart" size={21} />}
        />
        <MetricCard
          title="قيد التنفيذ"
          value={String(stats.processing ?? 0)}
          icon={<Icon name="refresh" size={21} />}
        />
        <MetricCard
          title="ناجحة"
          value={String(stats.success_count ?? 0)}
          icon={<Icon name="check" size={21} />}
        />
        <MetricCard
          title="فاشلة/مرفوضة"
          value={String(stats.failed_count ?? 0)}
          hint={
            stats.rejected_count != null
              ? `منها مرفوض: ${stats.rejected_count}`
              : undefined
          }
          icon={<Icon name="close" size={21} />}
        />
        <MetricCard
          title="مسترجعة"
          value={String(stats.refunded_count ?? 0)}
          icon={<Icon name="wallet" size={21} />}
        />
      </MetricGrid>

      <FilterCard title="البحث والتصفية" icon={<Icon name="search" size={24} />}>
        <ProSearch
          value={q}
          onChange={setQ}
          placeholder="بحث برقم الطلب، الاسم، الهاتف أو الرقم المستهدف"
        />
        <Select
          label="الحالة"
          value={status}
          onChange={setStatus}
          options={[
            { value: 'all', label: 'كل الحالات' },
            { value: 'unsuccessful', label: 'غير الناجحة' },
            { value: 'processing', label: 'قيد التنفيذ' },
            { value: 'success', label: 'ناجح' },
            { value: 'failed', label: 'فاشل / مرفوض' },
            { value: 'rejected', label: 'مرفوض فقط' },
            { value: 'refunded', label: 'مسترجع' },
          ]}
        />
        <Select
          label="الشبكة"
          value={company}
          onChange={setCompany}
          options={[
            { value: 'all', label: 'كل الشبكات' },
            { value: 'syriatel', label: 'سيريتل' },
            { value: 'mtn', label: 'MTN' },
          ]}
        />
        <Select
          label="الخدمة"
          value={kind}
          onChange={setKind}
          options={[
            { value: 'all', label: 'كل الخدمات' },
            { value: 'units', label: 'وحدات' },
            { value: 'bills', label: 'فواتير' },
            { value: 'cash', label: 'كاش' },
          ]}
        />
        <Select
          label="المصدر"
          value={source}
          onChange={setSource}
          options={[
            { value: 'all', label: 'كل المصادر' },
            { value: 'sham_balance', label: 'Sham Balance' },
            { value: 'kazeti_ussd', label: 'USSD' },
            { value: 'api_syria', label: 'API Syria' },
          ]}
        />
      </FilterCard>

      <SectionCard
        title={`طلبات الوحدات — الصفحة ${currentPage} من ${meta.pages || 1}`}
        subtitle="يتم عرض 100 طلب في كل صفحة والتحديث تلقائياً كل 4 ثوانٍ."
        icon={<Icon name="orders" size={24} />}
      >
        {rows.length === 0 ? (
          <Empty title="لا توجد طلبات مطابقة" hint="جرّب تغيير الفلاتر أو عبارة البحث." />
        ) : (
          <ListTable
            tableClassName="unit-orders-table"
            headers={[
              'الطلب',
              'الزبون',
              'الشبكة/الخدمة',
              'الرقم',
              'القيمة',
              'السعر',
              'المصدر',
              'الجهاز',
              'الحالة',
              'إجراءات',
            ]}
          >
            <tbody>
              {rows.map((order) => {
                const review = needsManualReview(order);
                const displayStatus = str(order.display_status ?? order.status);
                const busy = busyId === str(order.id);
                const showRetry = canRetryOrder(order);
                const showRefund = canRefundOrder(order);
                return (
                  <tr
                    key={str(order.id)}
                    className={`order-row ${review || isFailedLike(order) ? 'needs-review-row' : ''}`.trim()}
                    onClick={() => setSelected(order)}
                  >
                    <td>
                      <strong>#{str(order.id)}</strong>
                    </td>
                    <td>
                      <strong>{orderCustomerName(order)}</strong>
                    </td>
                    <td>
                      <strong>{labelOf(COMPANY_LABELS, order.company, '-')}</strong>
                      <small>{labelOf(KIND_LABELS, order.operation_kind, '-')}</small>
                    </td>
                    <td>
                      <strong dir="ltr" lang="en">
                        {str(order.target)}
                      </strong>
                    </td>
                    <td>
                      <span className="num" dir="ltr" lang="en">
                        {formatNumber(order.requested_amount ?? order.amount ?? 0)}
                      </span>
                    </td>
                    <td>
                      <Money value={order.cost ?? order.sale_price} />
                    </td>
                    <td>{labelOf(SOURCE_LABELS, order.source_provider, 'غير محدد')}</td>
                    <td>
                      <span className="executor-short">{shortExecutorLabel(order)}</span>
                    </td>
                    <td>
                      <StatusBadge status={displayStatus} />
                      {review ? <small>تحقق يدوي</small> : null}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="icon-btn"
                          title="تفاصيل الطلب"
                          onClick={() => setSelected(order)}
                        >
                          <Icon name="search" size={16} />
                        </button>
                        {review ? (
                          <button
                            type="button"
                            className="icon-btn ok"
                            disabled={busy}
                            title="اعتماد كناجح"
                            onClick={() => void markSuccess(order)}
                          >
                            <Icon name="check" size={16} />
                          </button>
                        ) : null}
                        {showRetry ? (
                          <button
                            type="button"
                            className="icon-btn"
                            disabled={busy}
                            title="إعادة المحاولة"
                            onClick={() => void retryOrder(order)}
                          >
                            <Icon name="refresh" size={16} />
                          </button>
                        ) : null}
                        {showRefund ? (
                          <button
                            type="button"
                            className="icon-btn danger"
                            disabled={busy}
                            title="إلغاء وإرجاع المبلغ"
                            onClick={() => void rejectOrder(order)}
                          >
                            <Icon name="close" size={16} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ListTable>
        )}

        <div className="pagination-bar">
          <Button
            type="button"
            variant="secondary"
            disabled={currentPage <= 1}
            onClick={() => setPage(Math.max(1, currentPage - 1))}
          >
            السابق
          </Button>
          <span>
            عرض {rows.length} من {meta.total || 0}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={currentPage >= (meta.pages || 1)}
            onClick={() => setPage(Math.min(meta.pages || 1, currentPage + 1))}
          >
            التالي
          </Button>
        </div>
      </SectionCard>

      <Modal
        open={!!selected}
        wide
        title={selected ? `تفاصيل الطلب #${str(selected.id)}` : 'تفاصيل الطلب'}
        subtitle={selected ? formatDateTime(selected.created_at) : undefined}
        onClose={() => setSelected(null)}
      >
        {selected ? (
          <UnitOrderDetails
            order={selected}
            busy={busyId === str(selected.id)}
            onRetry={() => void retryOrder(selected)}
            onReject={() => void rejectOrder(selected)}
            onMarkSuccess={() => void markSuccess(selected)}
          />
        ) : null}
      </Modal>
    </ScreenFrame>
  );
}

function UnitOrderDetails({
  order,
  busy,
  onRetry,
  onReject,
  onMarkSuccess,
}: {
  order: JsonMap;
  busy: boolean;
  onRetry: () => void;
  onReject: () => void;
  onMarkSuccess: () => void;
}) {
  const review = needsManualReview(order);
  const isUssd = str(order.source_provider) === 'kazeti_ussd';
  const alias = orderCustomerAlias(order);
  const showRetry = canRetryOrder(order);
  const showRefund = canRefundOrder(order);
  const simLabel = str(
    order.executor_sim_label,
    order.executor_sim_slot == null ? '' : `SIM ${Number(order.executor_sim_slot) + 1}`,
  );

  return (
    <div className="unit-order-details">
      <div className="detail-grid section-card">
        <Row label="رقم الطلب" value={`#${str(order.id)}`} />
        <Row label="التاريخ والوقت" value={formatDateTime(order.created_at)} />
        <Row label="الزبون" value={orderCustomerName(order)} />
        {alias ? <Row label="اسم العرض" value={alias} /> : null}
        <Row
          label="هاتف الزبون"
          value={
            <span dir="ltr" lang="en">
              {str(order.phone, '—')}
            </span>
          }
        />
        <Row label="الشبكة" value={labelOf(COMPANY_LABELS, order.company)} />
        <Row label="الخدمة" value={labelOf(KIND_LABELS, order.operation_kind)} />
        <Row
          label="الرقم المستهدف"
          value={
            <span dir="ltr" lang="en">
              {str(order.target, '—')}
            </span>
          }
        />
        <Row
          label="القيمة"
          value={
            <span className="num" dir="ltr" lang="en">
              {formatNumber(order.requested_amount ?? order.amount ?? 0)}
            </span>
          }
        />
        <Row label="السعر" value={<Money value={order.cost ?? order.sale_price} />} />
        <Row label="المصدر" value={labelOf(SOURCE_LABELS, order.source_provider, 'غير محدد')} />
        <Row
          label="الحالة"
          value={
            <span className="cell-stack">
              <StatusBadge status={order.display_status ?? order.status} />
              {review ? <small>يحتاج تحققاً يدوياً</small> : null}
            </span>
          }
        />
        {order.request_label ? <Row label="وصف الطلب" value={str(order.request_label)} /> : null}
        {order.error_message ? <Row label="رسالة الخطأ" value={str(order.error_message)} /> : null}
        {order.review_message ? <Row label="ملاحظة المراجعة" value={str(order.review_message)} /> : null}
        {order.ussd_response ? <Row label="رد USSD" value={str(order.ussd_response)} /> : null}
      </div>

      {isUssd ? (
        <section className="section-card top-space">
          <div className="section-title">
            <div>
              <h2>جهاز التنفيذ</h2>
              <p>تفاصيل الجهاز والشريحة ومراحل التنفيذ.</p>
            </div>
            <span className="section-icon">
              <Icon name="device" size={22} />
            </span>
          </div>
          <div className="detail-grid">
            <Row
              label="الجهاز"
              value={str(
                order.executor_device_name,
                order.executor_device_id ? `جهاز #${str(order.executor_device_id)}` : 'بانتظار جهاز',
              )}
            />
            <Row
              label="معرّف الجهاز"
              value={order.executor_device_id ? `#${str(order.executor_device_id)}` : '—'}
            />
            <Row label="الشريحة" value={simLabel || '—'} />
            <Row label="مرحلة التنفيذ" value={labelOf(STAGE_LABELS, order.execution_stage)} />
            <Row
              label="وقت الاستلام"
              value={order.claimed_at ? formatDateTime(order.claimed_at) : '—'}
            />
            <Row
              label="بدء التنفيذ"
              value={order.execution_started_at ? formatDateTime(order.execution_started_at) : '—'}
            />
            <Row
              label="وقت النتيجة"
              value={order.completed_at ? formatDateTime(order.completed_at) : '—'}
            />
          </div>
        </section>
      ) : null}

      <div className="modal-actions">
        {review ? (
          <Button type="button" disabled={busy} onClick={onMarkSuccess}>
            <Icon name="check" size={16} /> اعتماد كناجح
          </Button>
        ) : null}
        {showRetry ? (
          <Button type="button" variant="secondary" disabled={busy} onClick={onRetry}>
            <Icon name="refresh" size={16} /> إعادة المحاولة
          </Button>
        ) : null}
        {showRefund ? (
          <Button type="button" variant="danger" disabled={busy} onClick={onReject}>
            <Icon name="close" size={16} /> إلغاء وإرجاع
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function GamesOrdersScreen() {
  const [rows, setRows] = useState<JsonMap[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [flash, setFlash] = useState('');
  const [responseDrafts, setResponseDrafts] = useState<Record<string, string>>({});

  const load = useCallback(async (silent = false) => {
    if (!silent) setError('');
    const res = await admin.productsOrders();
    if (!res.success) {
      if (!silent) {
        setError(errorMessage(res));
        setLoading(false);
      }
      return;
    }
    setRows(pickList(res, 'data', 'orders', 'items'));
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(true), 5000);
    return () => window.clearInterval(timer);
  }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((order) => {
      if (status !== 'all' && str(order.status) !== status) return false;
      if (!needle) return true;
      const hay = [
        str(order.product_name),
        str(order.user_name),
        str(order.user_phone),
        str(order.target_value ?? order.target ?? order.player_id),
        str(order.external_order_id),
        str(order.order_uuid),
        str(order.id),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, status]);

  const metrics = useMemo(() => {
    const processing = rows.filter((o) => str(o.status) === 'processing').length;
    const success = rows.filter((o) => str(o.status) === 'success').length;
    const failed = rows.filter((o) => str(o.status) === 'failed').length;
    const refunded = rows.filter((o) => str(o.status) === 'refunded').length;
    return { processing, success, failed, refunded, total: rows.length };
  }, [rows]);

  function draftValue(order: JsonMap): string {
    const id = str(order.id);
    if (Object.prototype.hasOwnProperty.call(responseDrafts, id)) {
      return responseDrafts[id] ?? '';
    }
    return productOrderNote(order);
  }

  async function checkApiStatuses() {
    setChecking(true);
    const res = await admin.productsOrdersCheck({ ids: [] });
    setChecking(false);
    setFlash(errorMessage(res, res.success ? 'تم تحديث حالات الطلبات.' : 'تعذر التحديث.'));
    void load(true);
  }

  async function retryOrder(id: unknown) {
    const res = await admin.productsOrdersRetry({ id, order_id: id });
    setFlash(errorMessage(res, res.success ? 'تمت العملية.' : 'تعذر إعادة الإرسال.'));
    if (res.success) void load(true);
  }

  async function resolveOrder(id: unknown, action: 'success' | 'refund') {
    const ok = window.confirm(
      action === 'success' ? 'اعتماد الطلب كناجح؟' : 'إرجاع مبلغ الطلب؟',
    );
    if (!ok) return;
    const res = await admin.productsOrdersResolve({ id, order_id: id, action });
    setFlash(errorMessage(res, res.success ? 'تمت العملية.' : 'تعذر معالجة الطلب.'));
    if (res.success) void load(true);
  }

  async function saveResponse(id: unknown) {
    const key = str(id);
    const res = await admin.productsOrdersResponse({
      id,
      response_text: responseDrafts[key] ?? '',
    });
    setFlash(errorMessage(res, res.success ? 'تم حفظ رد الطلب.' : 'تعذر حفظ الرد.'));
    if (res.success && res.data && typeof res.data === 'object') {
      const data = res.data as JsonMap;
      setRows((prev) =>
        prev.map((row) =>
          str(row.id) === key
            ? {
                ...row,
                response_json: data.response_json ?? row.response_json,
                api_response: data.api_response ?? row.api_response,
              }
            : row,
        ),
      );
    }
  }

  if (loading) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={() => void load()} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="game"
      title="طلبات الألعاب"
      subtitle="متابعة طلبات المنتجات الرقمية وحالة المصدر والملاحظات الداخلية الخاصة بالإدارة."
      actions={
        <Button type="button" disabled={checking} onClick={() => void checkApiStatuses()}>
          <Icon name="refresh" size={18} /> {checking ? 'جاري التحديث...' : 'تحديث حالات API'}
        </Button>
      }
    >
      <FlashMessage message={flash} />

      <MetricGrid>
        <MetricCard
          title="كل الطلبات"
          value={String(metrics.total)}
          icon={<Icon name="orders" size={21} />}
        />
        <MetricCard
          title="قيد المعالجة"
          value={String(metrics.processing)}
          icon={<Icon name="refresh" size={21} />}
        />
        <MetricCard
          title="ناجحة"
          value={String(metrics.success)}
          icon={<Icon name="check" size={21} />}
        />
        <MetricCard
          title="فاشلة/مسترجعة"
          value={`${metrics.failed} / ${metrics.refunded}`}
          icon={<Icon name="close" size={21} />}
        />
      </MetricGrid>

      <FilterCard title="تصفية الطلبات" icon={<Icon name="search" size={24} />}>
        <ProSearch
          value={q}
          onChange={setQ}
          placeholder="بحث باسم الزبون أو المنتج أو ID"
        />
        <Select
          label="الحالة"
          value={status}
          onChange={setStatus}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'processing', label: 'قيد المعالجة' },
            { value: 'success', label: 'ناجح' },
            { value: 'failed', label: 'فاشل' },
            { value: 'refunded', label: 'مسترجع' },
          ]}
        />
        <Button type="button" variant="secondary" onClick={() => void load()}>
          <Icon name="refresh" size={17} /> تحديث القائمة
        </Button>
      </FilterCard>

      <SectionCard title="كل الطلبات" icon={<Icon name="chart" size={24} />}>
        {filtered.length === 0 ? (
          <Empty title="لا توجد طلبات مطابقة" hint="عدّل التصفية أو حدّث القائمة." />
        ) : (
          <ListTable
            className="top-space"
            tableClassName="game-orders-table"
            headers={['الطلب', 'الزبون', 'المنتج', 'الهدف', 'السعر', 'الحالة', 'رد API', 'إجراءات']}
          >
            <tbody>
              {filtered.map((order) => {
                const id = str(order.id);
                const open = ['processing', 'failed'].includes(str(order.status));
                return (
                  <tr key={id}>
                    <td>
                      <strong>#{id}</strong>
                      <small>{formatDateTime(order.created_at)}</small>
                      <small>{str(order.external_order_id ?? order.order_uuid, '-')}</small>
                    </td>
                    <td>
                      <strong>{str(order.user_name)}</strong>
                      <small dir="ltr" lang="en">
                        {str(order.user_phone)}
                      </small>
                    </td>
                    <td>
                      <strong>{str(order.product_name ?? order.name)}</strong>
                      <small>{str(order.provider_name, '-')}</small>
                    </td>
                    <td>
                      <span dir="ltr" lang="en">
                        {str(order.target_value ?? order.target ?? order.player_id, '-')}
                      </span>
                    </td>
                    <td>
                      <Money
                        value={order.customer_price ?? order.price ?? order.cost}
                        suffix="$"
                        fractionDigits={2}
                      />
                    </td>
                    <td>
                      <StatusBadge status={order.status} />
                      <small>{providerStatusLabel(order.provider_status)}</small>
                    </td>
                    <td className="api-response-cell">
                      <Textarea
                        className="inline-response-editor"
                        label="رد الإدارة"
                        rows={3}
                        value={draftValue(order)}
                        onChange={(v) =>
                          setResponseDrafts((prev) => ({ ...prev, [id]: v }))
                        }
                        placeholder="ملاحظة داخلية للإدارة"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        className="mini-btn"
                        onClick={() => void saveResponse(order.id)}
                      >
                        <Icon name="edit" size={14} /> حفظ الرد
                      </Button>
                    </td>
                    <td>
                      {open ? (
                        <div className="table-actions">
                          {str(order.provider_status) === 'waiting_provider_balance' ? (
                            <button
                              type="button"
                              className="icon-btn"
                              title="إعادة إرسال للمصدر"
                              onClick={() => void retryOrder(order.id)}
                            >
                              <Icon name="zap" size={16} />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="icon-btn ok"
                            title="اعتماد ناجح"
                            onClick={() => void resolveOrder(order.id, 'success')}
                          >
                            <Icon name="check" size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            title="إرجاع المبلغ"
                            onClick={() => void resolveOrder(order.id, 'refund')}
                          >
                            <Icon name="close" size={16} />
                          </button>
                        </div>
                      ) : (
                        <small>مكتمل</small>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ListTable>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
